import { isEmpty } from "lodash";
import { genericOverrideURL } from "./Utils";
import {
  birthDateOverrideString,
  dobOverriddenByString,
  dobOverrideReasonString,
  dobOverrideTimestampString,
  genderOverrideString,
  genderOverriddenByString,
  genderOverrideReasonString,
  genderOverrideTimestampString
} from "./Utils";

const AttributeValues = (attribute, records) => {
  const setAddressValues = (address, valueMap) => {
    const addressMap = new Map(valueMap);
    if (address.hasOwnProperty("extension")) {
      addressMap.set("extensionObject", address["extension"]);
      address["extension"]
        .filter(e => e.hasOwnProperty("url"))
        .forEach(e => {
          if (e["url"].includes("attrCode")) {
            addressMap.set("attrCode", e["valueString"]);
          }
          if (e["url"].includes("override")) {
            addressMap.set("overridePresent", true);
          }
        });
    }

    if (address.hasOwnProperty("use")) {
      addressMap.set("use", address["use"]);
    }
    if (address.hasOwnProperty("line")) {
      addressMap.set("line", address["line"]);
    }
    if (address.hasOwnProperty("city")) {
      addressMap.set("city", address["city"]);
    }
    if (address.hasOwnProperty("district")) {
      addressMap.set("district", address["district"]);
    }
    if (address.hasOwnProperty("state")) {
      addressMap.set("state", address["state"]);
    }
    if (address.hasOwnProperty("country")) {
      addressMap.set("country", address["country"]);
    }
    if (address.hasOwnProperty("postalCode")) {
      addressMap.set("postalCode", address["postalCode"]);
    }
    if (address.hasOwnProperty("period")) {
      addressMap.set("period", address["period"]);
    }
    if (
      addressMap.has("line") ||
      addressMap.has("city") ||
      addressMap.has("district") ||
      addressMap.has("state") ||
      addressMap.has("country") ||
      addressMap.has("postalCode")
    ) {
      addressMap.set(
        "displayValue",
        Array.from([
          addressMap.has("line") ? addressMap.get("line") : "[No Line]",
          addressMap.has("city") ? addressMap.get("city") : "[No City]",
          addressMap.has("district")
            ? addressMap.get("district")
            : "[No District]",
          addressMap.has("state") ? addressMap.get("state") : "[No State]",
          addressMap.has("country")
            ? addressMap.get("country")
            : "[No Country]",
          addressMap.has("postalCode")
            ? addressMap.get("postalCode")
            : "[No Postal Code]"
        ]).join(", ")
      );
    }
    return addressMap;
  };

  const setNameValues = (name, valueMap) => {
    const nameMap = new Map(valueMap);
    if (name.hasOwnProperty("extension")) {
      nameMap.set("extensionObject", name["extension"]);
      name["extension"]
        .filter(e => e.hasOwnProperty("url"))
        .forEach(e => {
          if (e["url"].includes("attrCode")) {
            nameMap.set("attrCode", e["valueString"]);
          }
          if (e["url"].includes("override")) {
            nameMap.set("overridePresent", true);
          }
        });
    }

    if (name.hasOwnProperty("use")) {
      nameMap.set("use", name["use"]);
    }
    if (name.hasOwnProperty("period")) {
      nameMap.set("period", name["period"]);
    }
    if (name.hasOwnProperty("family")) {
      nameMap.set("family", name["family"]);
    }
    if (name.hasOwnProperty("given")) {
      nameMap.set("given", name["given"]);
    }

    if (nameMap.has("family") || nameMap.has("given")) {
      nameMap.set(
        "displayValue",
        Array.from([
          nameMap.has("family") ? nameMap.get("family") : "[No Family]",
          nameMap.has("given")
            ? "[" + nameMap.get("given").join(", ") + "]"
            : "[No Given]"
        ]).join(", ")
      );
    }
    return nameMap;
  };

  const setTelecomValues = (telecom, attribute, valueMap) => {
    const telecomMap = new Map(valueMap);
    // Setting attrCode extension value.
    if (
      telecom.hasOwnProperty("system") &&
      telecom["system"] === attribute &&
      telecom.hasOwnProperty("extension")
    ) {
      telecomMap.set("extensionObject", telecom["extension"]);
      telecom["extension"]
        .filter(e => e.hasOwnProperty("url"))
        .forEach(e => {
          if (e["url"].includes("attrCode")) {
            telecomMap.set("attrCode", e["valueString"]);
          }
          if (e["url"].includes("override")) {
            telecomMap.set("overridePresent", true);
          }
        });
    }

    if (
      telecom.hasOwnProperty("system") &&
      telecom["system"] == attribute &&
      typeof telecom["value"] !== "undefined"
    ) {
      telecomMap
        .set(String(attribute), telecom["value"])
        .set("displayValue", telecom["value"]);
    }

    if (telecom.hasOwnProperty("period")) {
      telecomMap.set("period", telecom["period"]);
    }

    if (telecom.hasOwnProperty("use")) {
      telecomMap.set("use", telecom["use"]);
    }

    return telecomMap;
  };

  const setIdentifierValues = (identifier, valueMap) => {
    const identifierMap = new Map(valueMap);
    if (identifier.hasOwnProperty("extension")) {
      identifierMap.set("extensionObject", identifier["extension"]);
      identifier["extension"]
        .filter(e => e.hasOwnProperty("url"))
        .forEach(e => {
          if (e["url"].includes("override")) {
            identifierMap.set("overridePresent", true);
          }
        });
    }

    if (identifier.hasOwnProperty("use")) {
      identifierMap.set("use", identifier["use"]);
    }
    // We want to avoid overriding systems with the same value as the source system or "imdm".
    if (
      identifier.hasOwnProperty("system") &&
      identifier["system"] !== valueMap.get("source") &&
      identifier["system"] !== "imdm"
    ) {
      identifierMap.set("system", identifier["system"]);
    }

    if (identifier.hasOwnProperty("type")) {
      identifierMap.set("type", identifier["type"]);
    }

    if (
      identifier.hasOwnProperty("type") &&
      identifier["type"].hasOwnProperty("coding")
    ) {
      identifier["type"]["coding"].forEach(coding => {
        if (
          coding.hasOwnProperty("code") &&
          !["COV", "MR"].includes(coding["code"])
        ) {
          identifierMap.set("code", coding["code"]);
        }
      });
    }

    if (identifier.hasOwnProperty("value")) {
      identifierMap
        .set("value", identifier["value"])
        .set("displayValue", identifier["value"]);
    }
    if (identifier.hasOwnProperty("period")) {
      identifierMap.set("period", identifier["period"]);
    }
    return identifierMap;
  };

  // Returns a list of map() objects containing address values for each address in the address array.
  const setAddressMaps = (valueMap, addresses) => {
    const addressMapList = new Array();
    addresses.forEach(v => addressMapList.push(setAddressValues(v, valueMap)));
    return addressMapList;
  };

  // Returns a list of map() objects containing name values for each name in the name array.
  const setNameMaps = (valueMap, names) => {
    const nameMapList = new Array();
    names.forEach(a => nameMapList.push(setNameValues(a, valueMap)));
    return nameMapList;
  };

  // Returns a list of map() objects containing telecom values for each telecom in the telecom array.
  const setTelecomMaps = (valueMap, telecom, attribute) => {
    const telecomMapList = new Array();
    telecom.forEach(a =>
      telecomMapList.push(setTelecomValues(a, attribute, valueMap))
    );
    return telecomMapList;
  };

  // Returns a list of map() objects containing identifier values for each identifier in the identifier array.
  const setIdentifierMaps = (valueMap, identifier) => {
    const identifierMapList = new Array();
    identifier.forEach(v =>
      identifierMapList.push(setIdentifierValues(v, valueMap))
    );
    return identifierMapList;
  };

  // Returns a map() of birthDate values for each record.
  const setBirthDateMaps = (valueMap, record) => {
    const birthDateMap = new Map(valueMap);
    if (record.hasOwnProperty("extension")) {
      record["extension"]
        .filter(e => e.hasOwnProperty("url"))
        .forEach(e => {
          if (e["url"].includes("overrideBirthDate")) {
            birthDateMap.set("overridePresent", true);
          }
        });
    }

    birthDateMap
      .set("birthDate", record["birthDate"])
      .set("displayValue", record["birthDate"]);
    return birthDateMap;
  };

  // returns a map() of gender values for each record
  const setGenderMaps = (valueMap, record) => {
    const genderMap = new Map(valueMap);
    if (record.hasOwnProperty("extension")) {
      record["extension"]
        .filter(e => e.hasOwnProperty("url"))
        .forEach(e => {
          if (e["url"].includes("overrideGender")) {
            genderMap.set("overridePresent", true);
          }
        });
    }

    genderMap
      .set("gender", record["gender"])
      .set("displayValue", record["gender"]);
    return genderMap;
  };

  // Previous record override requests need to be included in subsequent override requests.
  //
  // Example: a birthDate is overridden on a record. Later, an address is overridden on the record.
  // The override request body for address override should include the birthDate override request as well.
  //
  // This function returns an Object() of previous override requests for each record. This can be used as a template
  // for subsequent override requests.
  const setPreviousOverrides = record => {
    const previousOverrides = new Object();
    Object.keys(record).forEach(key => {
      if (record[key] instanceof Array) {
        if (key === "extension") {
          record["extension"]
            .filter(e => e.hasOwnProperty("url"))
            .forEach(e => {
              // Add all DOB-related audit extensions
              const dobAuditUrls = [
                birthDateOverrideString,
                dobOverriddenByString,
                dobOverrideReasonString,
                dobOverrideTimestampString
              ];
              // Add all Gender-related audit extensions
              const genderAuditUrls = [
                genderOverrideString,
                genderOverriddenByString,
                genderOverrideReasonString,
                genderOverrideTimestampString
              ];

              if (dobAuditUrls.includes(e["url"])) {
                previousOverrides["extension"] =
                  previousOverrides["extension"] || new Array();
                previousOverrides["extension"].push(e);
                previousOverrides["birthDate"] = record["birthDate"];
              }
              if (genderAuditUrls.includes(e["url"])) {
                previousOverrides["extension"] =
                  previousOverrides["extension"] || new Array();
                previousOverrides["extension"].push(e);
                previousOverrides["gender"] = record["gender"];
              }
            });
        } else {
          record[key].forEach(item => {
            item.hasOwnProperty("extension") &&
              item["extension"].forEach(e => {
                if (
                  e.hasOwnProperty("url") &&
                  e["url"] === genericOverrideURL
                ) {
                  previousOverrides[key] =
                    previousOverrides[key] || new Array();
                  previousOverrides[key].push(item);
                }
              });
          });
        }
      }
    });
    return previousOverrides;
  };

  const valueList = new Array();
  if (!isEmpty(attribute)) {
    for (const [key, value] of records.entries()) {
      const initialValues = new Map()
        .set("id", value.id)
        .set("source", value.meta.source)
        .set("resourceType", value.resourceType)
        .set("meta", value.meta)
        .set("requestTemplate", setPreviousOverrides(value));
      if (value.hasOwnProperty(attribute)) {
        switch (attribute) {
          case "address":
            valueList.push(setAddressMaps(initialValues, value[attribute]));
            break;
          case "birthDate":
            valueList.push(setBirthDateMaps(initialValues, value));
            break;
          case "gender":
            valueList.push(setGenderMaps(initialValues, value));
            break;
          case "identifier":
            valueList.push(setIdentifierMaps(initialValues, value[attribute]));
            break;
          case "name":
            valueList.push(setNameMaps(initialValues, value[attribute]));
            break;
          default:
            valueList.push(initialValues);
            break;
        }
      } else if (value.hasOwnProperty("telecom")) {
        switch (attribute) {
          case "phone":
            valueList.push(
              setTelecomMaps(initialValues, value["telecom"], attribute)
            );
            break;
          case "email":
            valueList.push(
              setTelecomMaps(initialValues, value["telecom"], attribute)
            );
            break;
          default:
            valueList.push(initialValues);
            break;
        }
      } else {
        valueList.push(initialValues);
      }
    }
  }

  return valueList;
};

export default AttributeValues;
